import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Search,
  Filter,
  Package,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
  RefreshCw,
  Loader2
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to determine storage type based on NetSuite flags
const getStorageType = (item) => {
  const islotitem = item.islotitem === "T";
  const isserialitem = item.isserialitem === "T";
  const usebins = item.usebins === "T";
  
  // Determine base item type
  const itemType = item.itemtypetext || '';
  const isAssembly = itemType.toLowerCase().includes('assembly') || itemType.toLowerCase().includes('finished');
  const isInventory = itemType.toLowerCase().includes('inventory');

  // Check storage configurations and combine with item type
  if (islotitem) {
    if (isAssembly) {
      return "Lot-numbered Assembly Item";
    } else if (isInventory) {
      return "Lot-numbered Inventory Item";
    } else {
      return "Lot-numbered Item";
    }
  }
  
  if (isserialitem) {
    if (isAssembly) {
      return "Serial-numbered Assembly Item";
    } else if (isInventory) {
      return "Serial-numbered Inventory Item";
    } else {
      return "Serial-numbered Item";
    }
  }
  
  if (usebins) {
    if (isAssembly) {
      return "Bin-managed Assembly Item";
    } else if (isInventory) {
      return "Bin-managed Inventory Item";
    } else {
      return "Bin-managed Storage";
    }
  }

  // No special storage flags - base on item type
  if (isAssembly) {
    return "Standard Assembly Item";
  } else if (isInventory) {
    return "Standard/Regular Inventory";
  } else {
    return "Standard Item";
  }
};

const ItemsPage = ({ items: propItems, onItemsRefresh }) => {
  const [items, setItems] = useState(propItems || []);
  const [loading, setLoading] = useState(!propItems || propItems.length === 0);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [bomSyncLoading, setBomSyncLoading] = useState(false);
  const [dataRefreshLoading, setDataRefreshLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // BOM related state
  const [selectedItemBOM, setSelectedItemBOM] = useState(null);
  const [bomLoading, setBomLoading] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    itemid: "",
    costcategoryname: "",
    itemtypetext: "",
    storagetype: ""
  });

  // Sync with props when they change
  useEffect(() => {
    if (propItems && propItems.length > 0) {
      setItems(propItems);
      setLoading(false);
    }
  }, [propItems]);

  // Fallback fetch if no props provided
  useEffect(() => {
    if (!propItems || propItems.length === 0) {
      fetchItems();
    }
  }, [propItems]);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/items`);
      setItems(response.data);
    } catch (error) {
      console.error("Error fetching items:", error);
      toast.error("Failed to fetch items");
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch BOM data for selected item
  const fetchItemBOM = async (itemId) => {
    if (!itemId) return;
    
    setBomLoading(true);
    try {
      const response = await axios.get(`${API}/items/${itemId}/bom`);
      setSelectedItemBOM(response.data);
    } catch (error) {
      console.error("Error fetching BOM:", error);
      setSelectedItemBOM(null);
      // Don't show error toast for BOM - it's optional data
    } finally {
      setBomLoading(false);
    }
  };

  // Get unique values for filter dropdowns
  const uniqueProductGroups = useMemo(() => [...new Set(items.map(i => i.costcategoryname).filter(Boolean))], [items]);
  const uniqueItemTypes = useMemo(() => [...new Set(items.map(i => i.itemtypetext).filter(Boolean))], [items]);
  const uniqueStorageTypes = useMemo(() => [...new Set(items.map(getStorageType))], [items]);

  // Filter and search logic
  const filteredItems = useMemo(() => {
    let result = items;

    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(item => 
        item.itemid.toLowerCase().includes(query) ||
        item.displayname.toLowerCase().includes(query) ||
        item.costcategoryname.toLowerCase().includes(query) ||
        item.itemtypetext.toLowerCase().includes(query)
      );
    }

    // Apply filters
    if (filters.itemid) {
      result = result.filter(item => item.itemid.toLowerCase().includes(filters.itemid.toLowerCase()));
    }
    if (filters.costcategoryname) {
      result = result.filter(item => item.costcategoryname === filters.costcategoryname);
    }
    if (filters.itemtypetext) {
      result = result.filter(item => item.itemtypetext === filters.itemtypetext);
    }
    if (filters.storagetype) {
      result = result.filter(item => getStorageType(item) === filters.storagetype);
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        const aStr = String(aValue || '').toLowerCase();
        const bStr = String(bValue || '').toLowerCase();
        
        if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [items, searchQuery, filters, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-4 h-4 ml-1 opacity-30" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1" /> 
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  const handleRowClick = async (item) => {
    setSelectedItem(item);
    setShowItemModal(true);
    setSelectedItemBOM(null); // Reset BOM data
    
    // Fetch BOM data for this item
    if (item.id) {
      await fetchItemBOM(item.id);
    }
  };

  const clearFilters = () => {
    setFilters({
      itemid: "",
      costcategoryname: "",
      itemtypetext: "",
      storagetype: ""
    });
    setShowFilterModal(false);
  };

  const handleRefreshFromNetSuite = async () => {
    if (refreshLoading) return; // Prevent multiple clicks

    setRefreshLoading(true);
    try {
      const response = await axios.post(`${API}/sync/items`);
      if (response.status === 202) {
        toast.success("Item sync started successfully. The list will update automatically.");
        // WebSocket will handle the automatic refresh
      }
    } catch (error) {
      console.error("Error triggering NetSuite sync:", error);
      toast.error("Failed to start item sync.");
    } finally {
      setRefreshLoading(false);
    }
  };

  const handleBomSync = async () => {
    if (bomSyncLoading) return; // Prevent multiple clicks

    setBomSyncLoading(true);
    try {
      const response = await axios.post(`${API}/sync/boms`);
      if (response.status === 202) {
        toast.success("BOM sync started successfully. BOM data will be updated.");
      }
    } catch (error) {
      console.error("Error triggering BOM sync:", error);
      toast.error("Failed to start BOM sync.");
    } finally {
      setBomSyncLoading(false);
    }
  };

  const handleDataRefresh = async () => {
    if (dataRefreshLoading) return; // Prevent multiple clicks

    setDataRefreshLoading(true);
    try {
      await fetchItems();
      toast.success("Item data refreshed successfully with latest inventory levels.");
    } catch (error) {
      console.error("Error refreshing item data:", error);
      toast.error("Failed to refresh item data.");
    } finally {
      setDataRefreshLoading(false);
    }
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== "");

  if (loading) {
    return (
      <div className="items-page" style={{ padding: '2rem' }}>
        <div className="text-center">Loading items...</div>
      </div>
    );
  }

  return (
    <div className="items-page" style={{ padding: '2rem', background: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: '2rem' }}>
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Package className="w-8 h-8" />
            Items
          </h1>
          <Badge variant="outline" className="text-sm">
            {filteredItems.length} of {items.length} items
          </Badge>
        </div>
        
        {/* Search and Filter Controls */}
        <div className="flex gap-3 mb-4">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search items by ID, name, product group, or type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button
            variant="outline"
            onClick={handleRefreshFromNetSuite}
            disabled={refreshLoading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshLoading ? 'animate-spin' : ''}`} />
            {refreshLoading ? 'Syncing Items...' : 'Sync Items'}
          </Button>
          <Button
            variant="outline"
            onClick={handleDataRefresh}
            disabled={dataRefreshLoading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${dataRefreshLoading ? 'animate-spin' : ''}`} />
            {dataRefreshLoading ? 'Refreshing...' : 'Refresh Data'}
          </Button>
          <Button
            variant="outline"
            onClick={handleBomSync}
            disabled={bomSyncLoading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${bomSyncLoading ? 'animate-spin' : ''}`} />
            {bomSyncLoading ? 'Syncing BOMs...' : 'Sync BOMs'}
          </Button>
          <Button
            variant={hasActiveFilters ? "default" : "outline"}
            onClick={() => setShowFilterModal(true)}
            className="flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            Filters
            {hasActiveFilters && (
              <Badge variant="secondary" className="ml-1">
                {Object.values(filters).filter(v => v !== "").length}
              </Badge>
            )}
          </Button>
        </div>
      </div>

      {/* Items Table */}
      <Card>
        <CardContent className="p-0">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
                <tr>
                  <th
                    style={{ padding: '1rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none', width: '80px' }}
                    onClick={() => handleSort('id')}
                  >
                    <div className="flex items-center font-semibold">
                      ID
                      {getSortIcon('id')}
                    </div>
                  </th>
                  <th
                    style={{ padding: '1rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none', minWidth: '250px' }}
                    onClick={() => handleSort('custom_sku')}
                  >
                    <div className="flex items-center font-semibold">
                      SKU
                      {getSortIcon('custom_sku')}
                    </div>
                  </th>
                  <th
                    style={{ padding: '1rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none', width: '35%' }}
                    onClick={() => handleSort('itemid')}
                  >
                    <div className="flex items-center font-semibold">
                      Item Name
                      {getSortIcon('itemid')}
                    </div>
                  </th>
                  <th 
                    style={{ padding: '1rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('product_group')}
                  >
                    <div className="flex items-center font-semibold">
                      Product Group
                      {getSortIcon('product_group')}
                    </div>
                  </th>
                  <th 
                    style={{ padding: '1rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('item_type_formatted')}
                  >
                    <div className="flex items-center font-semibold">
                      Item Type
                      {getSortIcon('item_type_formatted')}
                    </div>
                  </th>
                  <th 
                    style={{ padding: '1rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('storage_type')}
                  >
                    <div className="flex items-center font-semibold">
                      Storage Type
                      {getSortIcon('storage_type')}
                    </div>
                  </th>
                  <th 
                    style={{ padding: '1rem', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('total_quantity_on_hand')}
                  >
                    <div className="flex items-center justify-end font-semibold">
                      Total Inventory
                      {getSortIcon('total_quantity_on_hand')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ padding: '3rem', textAlign: 'center', color: '#6c757d' }}>
                      <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No items found</p>
                      {(searchQuery || hasActiveFilters) && (
                        <p className="text-sm mt-2">Try adjusting your search or filters</p>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item) => (
                    <tr 
                      key={item.id}
                      onClick={() => handleRowClick(item)}
                      style={{ 
                        borderBottom: '1px solid #e9ecef',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {/* ID Column (Restored) */}
                      <td style={{ padding: '1rem' }}>
                        <span className="font-mono text-sm text-gray-500">{item.id}</span>
                      </td>

                      {/* SKU Column (Wider based on header) */}
                      <td style={{ padding: '1rem' }}>
                        <span className="font-mono text-sm font-medium">{item.custom_sku || ''}</span>
                      </td>
                      
                      {/* Item Name Column */}
                      <td style={{ padding: '1rem' }}>
                        <div className="font-medium">{item.itemid}</div>
                        {item.displayname && item.displayname !== item.itemid && (
                          <div className="text-xs text-gray-500">{item.displayname}</div>
                        )}
                      </td>

                      {/* Product Group Column */}
                      <td style={{ padding: '1rem' }}>
                        {item.product_group ? (
                          <Badge variant="outline">{item.product_group}</Badge>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>

                      {/* Item Type Column */}
                      <td style={{ padding: '1rem' }}>
                        <Badge 
                          variant={item.item_type_formatted?.includes('Assembly') ? 'secondary' : 'default'}
                        >
                          {item.item_type_formatted || item.itemtype || 'Item'}
                        </Badge>
                      </td>

                      {/* Storage Type Column */}
                      <td style={{ padding: '1rem' }}>
                        {item.storage_type ? (
                          <span className="text-sm text-gray-700">{item.storage_type}</span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>

                      {/* Inventory Column */}
                      <td style={{ padding: '1rem', textAlign: 'right' }}>
                        <span className="font-semibold">{item.total_quantity_on_hand?.toLocaleString() || '0'}</span>
                        <span className="text-sm text-gray-500 ml-1">
                          {item.stock_unit_name || item.unitofmeasure || 'units'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Filter Modal */}
      <Dialog open={showFilterModal} onOpenChange={setShowFilterModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filter Items
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Item Name (Item ID)</label>
              <Input
                placeholder="Filter by item name..."
                value={filters.itemid}
                onChange={(e) => setFilters({...filters, itemid: e.target.value})}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Product Group</label>
              <select
                value={filters.costcategoryname}
                onChange={(e) => setFilters({...filters, costcategoryname: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">All Product Groups</option>
                {uniqueProductGroups.map(group => (
                  <option key={group} value={group}>{group}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Item Type</label>
              <select
                value={filters.itemtypetext}
                onChange={(e) => setFilters({...filters, itemtypetext: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">All Types</option>
                {uniqueItemTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Storage Type</label>
              <select
                value={filters.storagetype}
                onChange={(e) => setFilters({...filters, storagetype: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">All Storage Types</option>
                {uniqueStorageTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="flex gap-2 justify-end mt-6">
            <Button variant="outline" onClick={clearFilters}>
              Clear All
            </Button>
            <Button onClick={() => setShowFilterModal(false)}>
              Apply Filters
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Item Detail Modal */}
      {selectedItem && (
        <Dialog open={showItemModal} onOpenChange={setShowItemModal}>
          <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl">
                {selectedItem.itemid || selectedItem.displayname || 'Item Details'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 mt-6">
              {/* Basic Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Basic Information</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-sm font-medium text-gray-500">SKU</div>
                    <div className="mt-1">
                      {selectedItem.custom_sku ? (
                        <span className="font-mono text-lg font-bold text-gray-900">{selectedItem.custom_sku}</span>
                      ) : (
                        <span className="text-red-500 italic text-sm bg-red-50 px-2 py-1 rounded">
                          Please fill SKU in NetSuite (custitem1)
                        </span>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-medium text-gray-500">Item Name / Number</div>
                    <div className="mt-1 font-medium">{selectedItem.itemid}</div>
                  </div>

                  <div>
                    <div className="text-sm font-medium text-gray-500">Product Group</div>
                    <div className="mt-1">
                      {selectedItem.product_group ? (
                        <Badge variant="outline" className="text-sm">{selectedItem.product_group}</Badge>
                      ) : (
                        <span className="text-red-500 italic text-sm">Please select Product Group in NetSuite</span>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-medium text-gray-500">Storage Type</div>
                    <div className="mt-1">
                      {selectedItem.storage_type ? (
                        <span className="text-gray-900">{selectedItem.storage_type}</span>
                      ) : (
                        <span className="text-red-500 italic text-sm">Please select Storage Type in NetSuite</span>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-medium text-gray-500">Stock Unit</div>
                    <div className="mt-1 font-medium text-gray-900">
                      {selectedItem.stock_unit_name || selectedItem.unitofmeasure || "N/A"}
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-medium text-gray-500">NetSuite Item Type</div>
                    <div className="mt-1">
                      <Badge variant="secondary">{selectedItem.item_type_formatted || selectedItem.itemtype}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Inventory Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Inventory Information</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-sm text-gray-500">Unit of Measure</div>
                    <div className="font-semibold mt-1">{selectedItem.unitofmeasure || 'units'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Total On Hand</div>
                    <div className="text-lg font-bold text-blue-600 mt-1">
                      {(selectedItem.total_quantity_on_hand || 0).toLocaleString()} {selectedItem.unitofmeasure || 'units'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Storage Type</div>
                    <div className="mt-1">
                      <Badge variant="outline" className="text-xs">
                        {getStorageType(selectedItem)}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* BOM Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Bill of Materials (BOM)</CardTitle>
                </CardHeader>
                <CardContent>
                  {bomLoading ? (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Loading BOM data...</span>
                    </div>
                  ) : selectedItemBOM && selectedItemBOM.components && selectedItemBOM.components.length > 0 ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-green-700">
                        <Package className="w-4 h-4" />
                        <span className="font-medium">BOM Components ({selectedItemBOM.components.length} items)</span>
                      </div>
                      
                      {/* BOM Components Table */}
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                          <thead style={{ background: '#f8f9fa' }}>
                            <tr>
                              <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e9ecef' }}>Component ID</th>
                              <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e9ecef' }}>Component Name</th>
                              <th style={{ padding: '0.5rem', textAlign: 'right', borderBottom: '1px solid #e9ecef' }}>Quantity</th>
                              <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e9ecef' }}>Unit</th>
                              <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e9ecef' }}>Type</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedItemBOM.components.map((component, index) => (
                              <tr key={index}>
                                <td style={{ padding: '0.5rem', borderBottom: '1px solid #f1f3f4' }}>
                                  <span className="font-mono text-xs">{component.component_id}</span>
                                </td>
                                <td style={{ padding: '0.5rem', borderBottom: '1px solid #f1f3f4' }}>
                                  {component.component_name}
                                </td>
                                <td style={{ padding: '0.5rem', textAlign: 'right', borderBottom: '1px solid #f1f3f4' }}>
                                  <span className="font-semibold">{component.quantity?.toLocaleString() || '0'}</span>
                                </td>
                                <td style={{ padding: '0.5rem', borderBottom: '1px solid #f1f3f4' }}>
                                  <Badge variant="outline" className="text-xs">{component.unit || 'units'}</Badge>
                                </td>
                                <td style={{ padding: '0.5rem', borderBottom: '1px solid #f1f3f4' }}>
                                  <Badge 
                                    variant={component.item_type === 'food' ? 'default' : 'secondary'}
                                    className="text-xs"
                                  >
                                    {component.item_type || 'component'}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-orange-700">
                      <Package className="w-4 h-4" />
                      <span className="font-medium">No BOM found for this item</span>
                    </div>
                  )}
                  <p className="text-xs text-gray-600 mt-2">
                    BOM data is synced from NetSuite and updated through the BOM sync process.
                  </p>
                </CardContent>
              </Card>

              {/* NetSuite Reference */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-blue-700 text-sm">
                  <Package className="w-4 h-4" />
                  <span className="font-medium">NetSuite Reference</span>
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  This item is synced from NetSuite and cannot be edited directly. 
                  Any changes must be made in the NetSuite system.
                </p>
                <div className="mt-2 text-xs text-blue-500">
                  <strong>Internal ID:</strong> {selectedItem.id}<br />
                  <strong>Last Updated:</strong> Real-time sync from NetSuite
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ItemsPage;
